import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Zap,
  Menu,
  X,
  User,
  ShoppingCart,
  Award,
  Heart,
  BookmarkCheck,
  ClipboardList,
  Building2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { supabase } from '../lib/supabase';
import LoyaltyPointsDisplay from './LoyaltyPointsDisplay';
import LanguageToggle from './LanguageToggle';
import { useLoyaltyStore } from '../store/loyaltyStore';

export default function Navigation() {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [isStaff, setIsStaff] = useState(false);
  const { items: cartItems } = useCart();

  const { summary: loyaltySummary, isLoading: loyaltyLoading, fetchAndUpdateLoyaltySummary } = useLoyaltyStore();

  useEffect(() => {
    const checkStaffStatus = async () => {
      if (!user) {
        setIsStaff(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('staff_members')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);

        if (error) throw error;

        const { data: ownerData, error: ownerError } = await supabase
          .from('business_owners')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);

        if (ownerError) throw ownerError;

        setIsStaff((data && data.length > 0) || (ownerData && ownerData.length > 0));
      } catch (err) {
        console.error('Error checking staff status:', err);
        setIsStaff(false);
      }
    };

    checkStaffStatus();
    if (user) {
      fetchAndUpdateLoyaltySummary();
    }
  }, [user, fetchAndUpdateLoyaltySummary]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="bg-black/95 backdrop-blur-sm sticky top-0 z-50 border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <img src="/zaplogolong.png" alt="Zap" className="h-8 w-auto" />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                isActive('/') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/venues"
              className={`text-sm font-medium transition-colors ${
                isActive('/venues') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
              }`}
            >
              {t('nav.venues')}
            </Link>
            {user ? (
              <>
                <Link
                  to="/favorites/venues"
                  className={`text-sm font-medium transition-colors flex items-center ${
                    isActive('/favorites/venues') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                  }`}
                >
                  <Heart className="w-4 h-4 mr-1" />
                  {t('nav.savedVenues')}
                </Link>
                <Link
                  to="/favorites/orders"
                  className={`text-sm font-medium transition-colors flex items-center ${
                    isActive('/favorites/orders') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                  }`}
                >
                  <BookmarkCheck className="w-4 h-4 mr-1" />
                  {t('nav.savedOrders')}
                </Link>
                <Link
                  to="/order-history"
                  className={`text-sm font-medium transition-colors flex items-center ${
                    isActive('/order-history') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                  }`}
                >
                  <ClipboardList className="w-4 h-4 mr-1" />
                  {t('nav.orderHistory')}
                </Link>
                <Link
                  to="/business"
                  className={`text-sm font-medium transition-colors flex items-center ${
                    location.pathname.startsWith('/business') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                  }`}
                >
                  <Building2 className="w-4 h-4 mr-1" />
                  {t('nav.business')}
                </Link>

                {user && !isStaff && (
                  <div className="hidden md:flex items-center">
                    {/* Always display the loyalty link */}
                    <Link to="/loyalty" className="flex items-center space-x-2 bg-zinc-800/50 hover:bg-zinc-700/50 px-3 py-1.5 rounded-md transition-colors text-white">
                      {loyaltyLoading ? (
                        <div className="flex items-center">
                          <div className="w-4 h-4 rounded-full border-2 border-t-yellow-400 border-gray-600 animate-spin mr-1"></div>
                          <span className="text-sm">{t('nav.points')}</span>
                        </div>
                      ) : loyaltySummary ? (
                        <LoyaltyPointsDisplay
                          points={loyaltySummary.current_points}
                          tierName={loyaltySummary.tier_name}
                          tierColor={loyaltySummary.tier_color_hex}
                          compact
                        />
                      ) : (
                        <div className="flex items-center">
                          <Award className="w-4 h-4 mr-1 text-gray-400" />
                          <span className="text-sm">{t('nav.pointsWithCount', { count: 0 })}</span>
                        </div>
                      )}
                    </Link>
                  </div>
                )}

                {user && !isStaff && (
                  <Link 
                    to="/checkout" 
                    className={`hidden md:flex items-center relative text-gray-300 hover:text-white ${
                      cartItems.length === 0 ? 'pointer-events-none opacity-50' : ''
                    }`}
                  >
                    <ShoppingCart className="w-6 h-6" />
                    {cartItems && cartItems.length > 0 && (
                      <span className="absolute -top-2 -right-2 bg-yellow-400 text-black text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                      </span>
                    )}
                  </Link>
                )}

                <button
                  onClick={() => signOut()}
                  className="text-sm font-medium text-white hover:text-yellow-400 transition-colors"
                >
                  {t('nav.signOut')}
                </button>
                <Link
                  to="/profile"
                  className="p-2 text-white hover:text-yellow-400 transition-colors"
                >
                  <User className="w-5 h-5" />
                </Link>
                <LanguageToggle />
              </>
            ) : (
              <>
                <Link
                  to="/signin"
                  className="text-sm font-medium text-white hover:text-yellow-400 transition-colors"
                >
                  {t('nav.signIn')}
                </Link>
                <Link
                  to="/signup"
                  className="text-sm font-medium bg-yellow-400 text-black px-4 py-2 rounded-md hover:bg-yellow-300 transition-colors"
                >
                  {t('nav.signUp')}
                </Link>
                <LanguageToggle />
              </>
            )}
          </div>

          <div className="flex items-center md:hidden gap-3">
            <LanguageToggle />
            {user && !isStaff && (
              <Link
                to="/checkout"
                className={`relative text-gray-300 hover:text-white ${
                  cartItems.length === 0 ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                <ShoppingCart className="w-6 h-6" />
                {cartItems && cartItems.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-yellow-400 text-black text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                  </span>
                )}
              </Link>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-yellow-400 transition-colors"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-4">
            <Link
              to="/"
              className={`block text-sm font-medium transition-colors ${
                isActive('/') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/venues"
              className={`block text-sm font-medium transition-colors ${
                isActive('/venues') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.venues')}
            </Link>
            {user ? (
              <>
                <Link
                  to="/favorites/venues"
                  className={`block text-sm font-medium transition-colors flex items-center ${
                    isActive('/favorites/venues') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Heart className="w-4 h-4 mr-1" />
                  {t('nav.savedVenues')}
                </Link>
                <Link
                  to="/favorites/orders"
                  className={`block text-sm font-medium transition-colors flex items-center ${
                    isActive('/favorites/orders') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <BookmarkCheck className="w-4 h-4 mr-1" />
                  {t('nav.savedOrders')}
                </Link>
                <Link
                  to="/order-history"
                  className={`block text-sm font-medium transition-colors flex items-center ${
                    isActive('/order-history') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <ClipboardList className="w-4 h-4 mr-1" />
                  {t('nav.orderHistory')}
                </Link>
                <Link
                  to="/business"
                  className={`block text-sm font-medium transition-colors flex items-center ${
                    location.pathname.startsWith('/business') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Building2 className="w-4 h-4 mr-1" />
                  {t('nav.business')}
                </Link>
                <Link
                  to="/loyalty"
                  className={`block text-sm font-medium transition-colors flex items-center ${
                    isActive('/loyalty') ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Award className="w-4 h-4 mr-1"
                    style={{ color: loyaltySummary?.tier_color_hex || '#6B7280' }}
                  />
                  {t('nav.loyaltyPoints')}
                  <span className="ml-2 text-xs bg-black/20 rounded px-2 py-0.5">
                    {loyaltyLoading ? '...' :
                      (loyaltySummary?.current_points !== null && loyaltySummary?.current_points !== undefined) ?
                        loyaltySummary.current_points.toLocaleString() : '0'}
                  </span>
                </Link>
                <button
                  onClick={() => {
                    signOut();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left text-sm font-medium text-white hover:text-yellow-400 transition-colors"
                >
                  {t('nav.signOut')}
                </button>
                <Link
                  to="/profile"
                  className="block text-sm font-medium text-white hover:text-yellow-400 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.profile')}
                </Link>
                <LanguageToggle variant="inline" />
              </>
            ) : (
              <>
                <Link
                  to="/signin"
                  className="block text-sm font-medium text-white hover:text-yellow-400 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.signIn')}
                </Link>
                <Link
                  to="/signup"
                  className="block text-sm font-medium bg-yellow-400 text-black px-4 py-2 rounded-md hover:bg-yellow-300 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.signUp')}
                </Link>
                <LanguageToggle variant="inline" />
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}